import React, { useContext, useEffect, useState } from 'react';
import { FaCheck, FaUsers } from 'react-icons/fa';
import { Link } from 'react-router';
import { BASE_URL_API } from '../../../References/URLs';
import ActiveStoreContext from '../../../store/ActiveStoreContext';
import AuthContext from '../../../store/auth-context';
import styles from './CampaignAudience.module.css';
import { toast } from 'react-toastify';
import StepHeader from './shared/StepHeader';

const CampaignAudience = ({ data, setData, getCampaignDetails }) => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== "" ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const [searchQuery, setSearchQuery] = useState(''); // State for search filter
    const [selectedAudience, setSelectedAudience] = useState(null); // Track selected audience
    const [isLoadingAudiences, setIsLoadingAudiences] = useState(false)
    // The audience being previewed is tracked separately from the selected
    // one — Preview no longer selects/deselects, and always shows the card
    // that was actually clicked instead of whatever happens to be selected.
    const [previewAudience, setPreviewAudience] = useState(null)
    const [audienceData, setAudienceData] = useState([]);

    // Escape/overlay-click to close the preview drawer — scoped to when the
    // drawer is actually open, with proper cleanup (previously registered
    // directly in the render body on every render, leaking a new listener
    // each time and clobbering any other window.onclick handler in the app).
    useEffect(() => {
        if (!previewAudience) return

        const handleOverlayClick = (event) => {
            if (event.target.id === "apinccosac") setPreviewAudience(null)
        }
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setPreviewAudience(null)
        }

        window.addEventListener('click', handleOverlayClick)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('click', handleOverlayClick)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [previewAudience])

    const handleSelectAudience = async (audienceItem) => {
        const audienceIdentifier = audienceItem?.audienceIdentifier;

        // let's check if the audience is already selected or not...
        if (selectedAudience?.audienceIdentifier !== audienceIdentifier) {
            const updateCampaignAudience = await fetch(BASE_URL_API + "updateCampaignAudience",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore?.storeIdentifier,
                        campaignIdentifier: data?.campaignIdentifier,
                        audienceIdentifier: audienceIdentifier
                    }),
                });

            if (!updateCampaignAudience.ok) {
                toast.error(`Couldn't update the audience. Please try again.`);
                return
            } else {
                const updateCampaignAudienceRespo = await updateCampaignAudience.json();
                if (updateCampaignAudienceRespo.status === "success") {
                    toast(`Audience updated successfully!`)
                } else if (updateCampaignAudienceRespo?.message === "tokenExpired") {
                    authCtx.logout();
                    return
                } else {
                    toast.error(updateCampaignAudienceRespo?.message || `Failed to update audience. Please try again.`)
                    return
                }
            }
        }

        // Toggle audience selection
        const updatedAudienceData = audienceData.map((aud) =>
            aud?.audienceIdentifier === audienceItem?.audienceIdentifier
                ? { ...aud, isSelected: !aud?.isSelected }
                : aud
        );
        // Set selected audience in state
        setSelectedAudience(updatedAudienceData?.find((aud) => aud?.isSelected));
        // Real getCampaignDetails response carries this as audienceIdentifier
        // (+ audienceName, which can be null) — not a field called "audience"
        // as this used to write. Patched here optimistically so the sidebar
        // checkmark (which reads campaignData.audienceIdentifier) updates
        // immediately, then getCampaignDetails() re-syncs from the server —
        // same pattern MessageTemplates/TemplatePreview already use after a
        // successful template selection.
        setData({ ...data, audienceIdentifier: audienceItem?.audienceIdentifier, audienceName: audienceItem?.audienceName })
        getCampaignDetails?.()
    };

    const filteredAudienceData = audienceData?.filter((audienceItem) =>
        audienceItem.audienceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        audienceItem.audienceDescription.toLowerCase().includes(searchQuery.toLowerCase())
    );



    const getAllAudiences = async () => {
        setIsLoadingAudiences(true)
        const getAllAudiencesResponse = await fetch(BASE_URL_API + "getAllAudiences",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                }),
            });

        if (!getAllAudiencesResponse.ok) {
            toast.error(`Couldn't load audiences. Please try again.`);
            setAudienceData([])
        } else {
            const getAllAudiencesRespo = await getAllAudiencesResponse.json();
            if (getAllAudiencesRespo.status === "success") {
                setAudienceData(getAllAudiencesRespo?.response || []);
            } else if (getAllAudiencesRespo.message === "tokenExpired") {
                authCtx.logout();
                setAudienceData([])
            } else {
                toast.error(getAllAudiencesRespo?.message || `Couldn't load audiences. Please try again.`);
                setAudienceData([])
            }
        }
        setIsLoadingAudiences(false)
    };

    useEffect(() => {
        getAllAudiences()
    }, [])

    // The campaign may already have a saved audience (from a previous visit
    // to this step, or set before this component ever mounted) — without
    // this, selectedAudience only ever gets set by clicking a card in THIS
    // session, so a previously-selected audience never shows as selected
    // when reopening this screen. Runs whenever the real audience list or
    // the campaign's saved audienceIdentifier changes.
    useEffect(() => {
        if (!data?.audienceIdentifier || !audienceData?.length) return
        const matched = audienceData.find((aud) => aud?.audienceIdentifier === data.audienceIdentifier)
        if (matched) setSelectedAudience(matched)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.audienceIdentifier, audienceData])

    return (
        <div className={styles.container}>
            <StepHeader
                eyebrow="Step 3 of 5"
                title="Audience"
                subtitle="Choose who receives this campaign."
                right={
                    <Link
                        className="ccBtn ccBtnPrimary"
                        to={`/communications/audience/create-new?redirect=/communications/campaign/${data?.campaignIdentifier}/audience`}
                    >
                        Create New Audience
                    </Link>
                }
            />
            <div className={styles.headerWrapper}>
                <div className={styles.controlsWrapper}>
                    <div className={styles.searchAudienceWrapper}>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by audience name or description"
                            className={styles.searchInput}
                        />
                    </div>
                </div>
            </div>

            {isLoadingAudiences ? (
                <div className={styles.loadingState}>Loading audiences...</div>
            ) : filteredAudienceData?.length === 0 ? (
                <div className={styles.emptyState}>No audiences found. Create one to get started.</div>
            ) : (
                <div className={styles.audienceList}>
                    {filteredAudienceData?.map((audienceItem) => (
                        <div
                            key={audienceItem.audienceIdentifier}
                            className={`${styles.audienceCard} ${selectedAudience?.audienceIdentifier === audienceItem?.audienceIdentifier ? styles.selected : ''
                                }`}
                            onClick={() => handleSelectAudience(audienceItem)}
                        >
                            <div>
                                <div className={styles.nameRow}>
                                    <div className={styles.audienceName}>{audienceItem.audienceName}</div>
                                    <span className={`ccTag ${styles.sizeTag}`}>
                                        <FaUsers size={10} /> {(audienceItem?.audienceData?.length || 0).toLocaleString()}
                                    </span>
                                </div>
                                <div className={styles.audienceDescription}>{audienceItem.audienceDescription}</div>
                            </div>
                            <div className={styles.preview}>
                                {audienceItem?.audienceData?.slice(0, 2).map((user, index) => (
                                    <div key={index}>
                                        <p>{user.clientName} - {user.phoneNumber}</p>
                                    </div>
                                ))}
                                <div className={styles.totalCount}>
                                    {audienceItem?.audienceData?.length > 2 && <p>+ {audienceItem?.audienceData?.length - 2} more</p>}
                                </div>
                            </div>
                            <div className={styles.acardFooter}>
                                <div className={styles.createdOn}>
                                    Created on {audienceItem?.createdOn}
                                </div>
                                <div className={styles.actionWrapper}>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setPreviewAudience(audienceItem); }}
                                        className={styles.previewBtn}
                                    >
                                        Preview
                                    </button>
                                    {selectedAudience?.audienceIdentifier === audienceItem?.audienceIdentifier ?
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); handleSelectAudience(audienceItem); }}
                                            className={styles.selectBtnSelected}
                                        >
                                            <FaCheck /> Selected
                                        </button>
                                        :
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); handleSelectAudience(audienceItem); }}
                                            className={styles.selectBtn}
                                        >
                                            Select
                                        </button>
                                    }
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {previewAudience && (
                <div className={styles.selectedAudience}>
                    <div className={styles.selectedAudienceContainer} id="apinccosac">
                        <div className={styles.selectedAudienceContent}>
                            <button
                                type="button"
                                className={styles.closePreviewBtn}
                                onClick={() => setPreviewAudience(null)}
                                aria-label="Close preview"
                            >
                                ×
                            </button>
                            <div className={styles.audienceHeader}>
                                <div className={styles.audienceName} >{previewAudience.audienceName}</div>
                                <div className={styles.audienceDescription}>{previewAudience.audienceDescription}</div>
                            </div>
                            <div className={styles.sacContent}>
                                {previewAudience?.audienceData?.map((user, index) => (
                                    <div key={index} className={styles.eachItem} >
                                        <div className={styles.clientName} >{user?.clientName}</div>
                                        <div>{user?.phoneNumber}</div>
                                        <div>{user?.gender}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default CampaignAudience;
